import { useContext, useEffect, useState } from "react";
import { useAppDesignPdf, useBusyAction, useRequest } from "../../../hooks";
import {
  DownloadAction,
  EditAction,
} from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import { ClientProps, WellProps, DocRow } from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import DataContext from "../../../context/DataContext";
import { useNavigate } from "react-router-dom";
import { dateInRange, displayDate, fmtDate } from "../../../utils/docDate";

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const latestUploadByTallyId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    if (!row.tallyId) return;
    const cur = map.get(row.tallyId);
    if (!cur || new Date(row.uploadedAt ?? row.date).getTime() >
      new Date(cur.uploadedAt ?? cur.date).getTime())
      map.set(row.tallyId, row);
  });
  return map;
};

export const TallyDocAdmin = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { downloadTallyPdf } = useAppDesignPdf();
  const { clientOptions, fetchDataClient } = useContext(DataContext);
  const { paginationModel, setPaginationModel } = usePaginator("tally-doc-admin", 8);

  const [wells, setWells] = useState<WellProps[]>([]);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [source, setSource] = useState(""); // "" = all
  const [owner, setOwner] = useState(""); // "" = all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { fetchDataClient(); }, []);
  useEffect(() => { setTimeout(() => setIsError(false), 4000); }, [isError]);

  const handleClientChange = (client: ClientProps | null) => {
    setSelectedClient(client);
    setSelectedWellId(null);
    if (!client) { setWells([]); setRows([]); return; }
    const opts: RequestInit = { method: "GET" };
    setWellsLoading(true);
    setRowsLoading(true);
    Promise.all([
      handleRequest({ endpoint: `well/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `tally-list/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `tally-detail?client=${client.id}`, options: opts }),
    ])
      .then(([wellsRes, uploadedRes, appRes]) => {
        const wellData: WellProps[] = isSuccessful(wellsRes) ? wellsRes.data : [];
        setWells(wellData);
        setWellsLoading(false);
        const wellMap = new Map<number, string>(wellData.map((w) => [w.id as number, w.name]));
        const uploaded: DocRow[] = (isSuccessful(uploadedRes) ? uploadedRes.data : []).map(
          (item: any) => ({
            rowId: `u-${item.id}`,
            customName: item.customName,
            date: displayDate(item),
            uploadedAt: item.date ?? "",
            wellId: item.idWell,
            wellName: wellMap.get(item.idWell) ?? "",
            source: "uploaded" as const,
            path: item.path,
            owner: item.user?.name ?? "—",
            tallyId: item.tallyId,
          }),
        );
        const uploadsByTally = latestUploadByTallyId(uploaded);
        const app: DocRow[] = getRows(appRes).map((item: any) => {
          const def = uploadsByTally.get(item.id);
          return {
            rowId: `t-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: item.createdBy ?? "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        });
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
        setRowsLoading(false);
      })
      .catch(() => { setIsError(true); setWellsLoading(false); setRowsLoading(false); });
  };

  const fetchTallyByKey = (key: string, name: string) => {
    const opts: RequestInit = {
      method: "POST",
      body: JSON.stringify({ key }),
      headers: { responseType: "Buffer" } as any,
    };
    return handleRequest({ endpoint: "pdf-tally-adm", options: opts })
      .then((res) => {
        if (!res.success) { setIsError(true); return; }
        const url = window.URL.createObjectURL(
          new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
      })
      .catch(() => setIsError(true));
  };

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      await fetchTallyByKey(row.path, row.customName);
    } else if (row.source === "app") {
      if (row.defaultUploadPath) {
        await fetchTallyByKey(row.defaultUploadPath, row.defaultUploadName ?? row.customName);
      } else if (row.appId != null) {
        await downloadTallyPdf(row.appId, row.customName);
      }
    }
  };
  const { isBusy: isDownloading, run: runDownload } = useBusyAction(handleDownload);

  // ---- Derived: well filter -> search -> source/owner/date filters ----
  const filteredByWell =
    selectedWellId != null ? rows.filter((r) => r.wellId === selectedWellId) : rows;

  let filtered = docSearch.trim()
    ? filteredByWell.filter((r) =>
        r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()),
      )
    : filteredByWell;
  if (source) filtered = filtered.filter((r) => r.source === source);
  if (owner) filtered = filtered.filter((r) => r.owner === owner);
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      return dateInRange(r.date, dateFrom, dateTo);
    });
  }

  const ownerOptions = Array.from(new Set(rows.map((r) => r.owner))).filter(
    (o) => o && o !== "—",
  );

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Tally Design", render: (r) => r.customName, width: "2fr" },
    { key: "wellName", header: "Well", render: (r) => r.wellName || "—", width: "160px" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
    {
      key: "source",
      header: "Source",
      render: (r) => (r.source === "app" ? "App Design" : "Uploaded"),
      width: "110px",
    },
    { key: "owner", header: "Created by", render: (r) => r.owner, width: "160px" },
  ];

  const filters: ListFilterField[] = [
    {
      kind: "select",
      key: "source",
      label: "Source",
      value: source,
      onChange: setSource,
      options: [
        { value: "app", label: "App Design" },
        { value: "uploaded", label: "Uploaded" },
      ],
    },
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];
  if (ownerOptions.length > 0) {
    filters.splice(1, 0, {
      kind: "select",
      key: "owner",
      label: "Created by",
      value: owner,
      onChange: setOwner,
      options: ownerOptions.map((o) => ({ value: o, label: o })),
    });
  }

  return (
    <ListPageLayout<DocRow>
      title="Tally Designs"
      subtitle="Browse, filter and download tally designs"
      columns={columns}
      rows={pageRows}
      getRowId={(r) => r.rowId}
      rowsLoading={rowsLoading}
      isError={isError}
      emptyTitle={!selectedClient ? "Select a client to load their tally designs" : undefined}
      searchValue={docSearch}
      onSearchChange={setDocSearch}
      searchPlaceholder="Search tally designs…"
      filters={filters}
      page={currentPage}
      pageSize={paginationModel.pageSize}
      totalCount={totalCount}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      rowsPerPageOptions={[8, 15, 25, 50]}
      sidePanel={{
        title: "Wells",
        items: wells.map((w) => ({ id: w.id ?? -1, label: w.name })),
        selectedId: selectedWellId,
        onSelect: (id) => {
          setSelectedWellId(id as number | null);
          setPaginationModel({ ...paginationModel, page: 0 });
        },
        loading: wellsLoading,
        searchPlaceholder: "Search wells…",
      }}
      toolbarActions={
        clientOptions?.length ? (
          <select
            value={selectedClient ? String(selectedClient.id) : ""}
            onChange={(e) => {
              const c =
                clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
              handleClientChange(c);
            }}
          >
            <option value="" disabled>Select a client...</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        ) : undefined
      }
      rowActions={(r) => (
        <>
          <DownloadAction rowId={r.rowId} busy={isDownloading} onClick={() => runDownload(r)} />
          {r.source === "app" && (
            <EditAction
              rowId={r.rowId}
              title="Edit Design"
              onClick={() => navigate(`/tally/${r.appId}`)}
            />
          )}
        </>
      )}
    />
  );
};
